import { TextInput } from '../components/FormField';
import type { FormData } from '../types';

interface Props {
  data: FormData;
  onChange: (updates: Partial<FormData>) => void;
  readOnlyEmail?: boolean;
  errors?: Record<string, string>;
}

export function ContactStep({ data, onChange, readOnlyEmail, errors = {} }: Props) {
  return (
    <div>
      <div className="step-header">
        <h2>Primary Contact</h2>
        <p>This is the main person we will contact about jobs, payments, and any questions. This should be someone who can make decisions for your company.</p>
      </div>

      <div className="form-row">
        <TextInput
          label="First Name"
          required
          value={data.contactFirstName}
          error={errors.contactFirstName}
          placeholder="Example: John"
          onChange={(e) => onChange({ contactFirstName: e.target.value })}
        />
        <TextInput
          label="Last Name"
          required
          value={data.contactLastName}
          error={errors.contactLastName}
          placeholder="Example: Smith"
          onChange={(e) => onChange({ contactLastName: e.target.value })}
        />
      </div>

      <div className="form-group">
        <label>
          Email Address <span className="required">*</span>
        </label>
        <p className="field-hint">This is where we will send job details and payment notifications</p>
        <input
          type="email"
          value={data.contactEmail}
          onChange={readOnlyEmail ? undefined : (e) => onChange({ contactEmail: e.target.value })}
          readOnly={readOnlyEmail}
          style={readOnlyEmail ? { backgroundColor: '#F3F4F6', color: '#6B7280' } : undefined}
        />
        {readOnlyEmail && (
          <p style={{ fontSize: 12, color: 'var(--hts-success)', marginTop: 4, fontWeight: 500 }}>
            &#10003; Email verified
          </p>
        )}
        {errors.contactEmail && <p className="error-text">{errors.contactEmail}</p>}
      </div>

      <div className="form-row">
        <TextInput
          label="Phone Number"
          required
          type="tel"
          value={data.contactPhone}
          error={errors.contactPhone}
          placeholder="Example: (555) 123-4567"
          hint="Best number to reach this person"
          onChange={(e) => onChange({ contactPhone: e.target.value })}
        />
        <TextInput
          label="Title / Position"
          value={data.contactTitle}
          placeholder="Example: Owner, Manager, etc."
          hint="Optional"
          onChange={(e) => onChange({ contactTitle: e.target.value })}
        />
      </div>

      <div className="form-group" style={{ marginTop: 8, paddingTop: 16, borderTop: '1px solid #E5E7EB' }}>
        <label className="agreement-label" style={{ alignItems: 'flex-start' }}>
          <input
            type="checkbox"
            checked={data.hasAlternativeSigner}
            onChange={(e) => onChange({ hasAlternativeSigner: e.target.checked })}
          />
          <span>
            Someone else is our authorized signer
            <span className="field-hint" style={{ display: 'block', marginTop: 2 }}>
              Check this if the person who signs contracts for your company is different from the contact above. You can fill out the form, and we will send the agreement to them to sign.
            </span>
          </span>
        </label>
      </div>

      {data.hasAlternativeSigner && (
        <div style={{ marginTop: 12 }}>
          <h3 style={{ color: '#0D2152', fontSize: 16, fontWeight: 600, margin: '0 0 4px' }}>Authorized Signer</h3>
          <p className="field-hint" style={{ marginBottom: 12 }}>
            We will email the agreement to this person to sign electronically.
          </p>

          <div className="form-row">
            <TextInput
              label="First Name"
              required
              value={data.signerFirstName}
              error={errors.signerFirstName}
              placeholder="Example: Jane"
              onChange={(e) => onChange({ signerFirstName: e.target.value })}
            />
            <TextInput
              label="Last Name"
              required
              value={data.signerLastName}
              error={errors.signerLastName}
              placeholder="Example: Doe"
              onChange={(e) => onChange({ signerLastName: e.target.value })}
            />
          </div>

          <div className="form-row">
            <TextInput
              label="Email Address"
              required
              type="email"
              value={data.signerEmail}
              error={errors.signerEmail}
              hint="Where we will send the agreement to sign"
              placeholder="Example: jane@company.com"
              onChange={(e) => onChange({ signerEmail: e.target.value })}
            />
            <TextInput
              label="Title / Position"
              value={data.signerTitle}
              placeholder="Example: Owner, President"
              hint="Optional"
              onChange={(e) => onChange({ signerTitle: e.target.value })}
            />
          </div>
        </div>
      )}
    </div>
  );
}
